// Fast'dan import (Q-034): eksport faylini tanlash → tekshirish (bazaga yozilmaydi) → import → rasmlarni ko'chirish.
// Faqat menyusi bo'sh restoran uchun. Xodimlarning yangi PIN'lari faqat shu oynada bir marta ko'rinadi.
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Alert, Button, CopyButton, Icon, Modal, TextAreaField } from '@onepos/ui';
import { api } from '../../lib/api';
import { FAST_EXPORT_SQL } from '../../lib/fastExport';

interface Counts {
  categories: number;
  products: number;
  modifier_groups: number;
  modifiers: number;
  raw_materials: number;
  recipes: number;
  stock: number;
  zones: number;
  tables: number;
  staff: number;
  meal_rules: number;
  settings: number;
  images: number;
}
interface Result {
  dry_run: boolean;
  counts: Counts;
  warnings: string[];
  staff: { name: string; role: string; pin?: string }[];
  images?: { product_id: string; url: string; name: string }[];
}

const ROWS: [keyof Counts, string][] = [
  ['categories', 'Kategoriyalar'],
  ['products', 'Mahsulotlar'],
  ['modifier_groups', 'Qo‘shimcha guruhlari'],
  ['modifiers', 'Qo‘shimchalar'],
  ['raw_materials', 'Xom ashyolar'],
  ['recipes', 'Retsept qatorlari'],
  ['stock', 'Qoldiq (boshlang‘ich)'],
  ['zones', 'Zonalar'],
  ['tables', 'Stollar'],
  ['staff', 'Xodimlar (yangi PIN bilan)'],
  ['meal_rules', 'Xodim ovqati qoidalari'],
  ['settings', 'Chek va kassa sozlamalari'],
  ['images', 'Rasmlar'],
];

/** Fayl: toza JSON yoki Supabase'dan "Export → CSV" (bitta ustun, sarlavha + qo'shtirnoqli qiymat) */
export function parseExport(raw: string): Record<string, unknown> {
  let t = raw.replace(/^\uFEFF/, '').trim();
  if (!t.startsWith('{')) {
    const nl = t.indexOf('\n');
    if (nl > 0 && /onepos_export/.test(t.slice(0, nl))) t = t.slice(nl + 1).trim();
    if (t.startsWith('"') && t.endsWith('"')) t = t.slice(1, -1).replace(/""/g, '"');
  }
  let data: unknown;
  try {
    data = JSON.parse(t);
  } catch {
    throw new Error('Fayl o‘qilmadi. Eksport natijasini (onepos_export) to‘liq nusxalang yoki CSV qilib yuklab oling.');
  }
  if (!data || typeof data !== 'object' || (data as { source?: unknown }).source !== 'fast') {
    throw new Error('Bu fayl fast eksporti emas. Pastdagi so‘rovni fast bazasida ishga tushiring.');
  }
  return data as Record<string, unknown>;
}

export function ImportFastModal({ tenantId, tenantName, onClose, onDone }: { tenantId: string; tenantName: string; onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [check, setCheck] = useState<Result | null>(null);
  const [done, setDone] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [img, setImg] = useState<{ ok: number; failed: string[]; total: number } | null>(null);

  const load = (raw: string) => {
    setError(undefined);
    setCheck(null);
    try {
      setData(parseExport(raw));
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      setError('Fayl juda katta (8 MB dan oshmasin).');
      return;
    }
    const raw = await f.text();
    setText('');
    load(raw);
  };

  const run = async (dry: boolean) => {
    if (!data) return;
    setBusy(true);
    setError(undefined);
    try {
      const res = await api.rpc<Result>('platform_import_fast', { p_tenant: tenantId, p_data: data, p_dry_run: dry });
      if (dry) setCheck(res);
      else {
        setDone(res);
        await copyImages(res.images ?? []);
        onDone();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Rasmlar bittadan-uchtadan: biri xato bo'lsa ham qolganlari ko'chadi
  const copyImages = async (list: NonNullable<Result['images']>) => {
    if (!list.length) return;
    const state = { ok: 0, failed: [] as string[], total: list.length };
    setImg({ ...state });
    let i = 0;
    const worker = async () => {
      while (i < list.length) {
        const it = list[i++]!;
        try {
          await api.invoke('import-image', { tenant_id: tenantId, product_id: it.product_id, url: it.url });
          state.ok += 1;
        } catch {
          state.failed.push(it.name);
        }
        setImg({ ...state, failed: [...state.failed] });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
  };

  const result = done ?? check;
  return (
    <Modal
      open
      wide
      title={`Fast’dan import — ${tenantName}`}
      onClose={onClose}
      busy={busy}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose} disabled={Boolean(img && img.ok + img.failed.length < img.total)}>
            Tayyor
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Bekor qilish
            </Button>
            {check ? (
              <Button variant="primary" onClick={() => void run(false)} disabled={busy}>
                {busy ? 'Import qilinmoqda…' : 'Import qilish'}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => void run(true)} disabled={busy || !data}>
                {busy ? 'Tekshirilmoqda…' : 'Tekshirish'}
              </Button>
            )}
          </>
        )
      }
    >
      {!done && (
        <>
          <ol className="cn-import__steps">
            <li>
              Fast ishlayotgan Supabase loyihasida <b>SQL Editor</b>ni oching va quyidagi so‘rovni ishga tushiring. So‘rov
              faqat o‘qiydi — hech narsani o‘zgartirmaydi. PIN, parol va savdo tarixi olinmaydi.
              <div className="cn-row" style={{ marginTop: 8 }}>
                <CopyButton text={FAST_EXPORT_SQL} label="So‘rovni nusxalash" />
                <a
                  className="op-btn op-btn--ghost op-btn--sm"
                  download="onepos-fast-eksport.sql"
                  href={`data:text/plain;charset=utf-8,${encodeURIComponent(FAST_EXPORT_SQL)}`}
                >
                  <Icon name="download" /> .sql fayl
                </a>
              </div>
            </li>
            <li>
              Natijadagi <b>onepos_export</b> katagini yuklab oling (<i>Export → CSV</i>) yoki nusxalab shu yerga qo‘ying.
            </li>
          </ol>
          <div className="cn-import__src">
            <label className="op-btn op-btn--secondary">
              <Icon name="download" /> Faylni tanlash
              <input type="file" accept=".json,.txt,.csv,application/json,text/plain,text/csv" hidden onChange={(e) => void onFile(e)} />
            </label>
            <span className="cn-muted">yoki</span>
          </div>
          <TextAreaField
            label="Natijani qo‘ying"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (e.target.value.trim()) load(e.target.value);
            }}
            rows={3}
            placeholder='{"source":"fast", ...}'
          />
          {data && !check && <Alert tone="info">Fayl o‘qildi. Endi “Tekshirish” — bazaga hech narsa yozilmaydi.</Alert>}
        </>
      )}
      {error && <Alert tone="danger">{error}</Alert>}

      {result && (
        <section className="cn-import__result" aria-live="polite">
          <h3>{done ? 'Import qilindi' : 'Tekshiruv natijasi — hali hech narsa yozilmadi'}</h3>
          <dl className="cn-import__counts">
            {ROWS.filter(([k]) => result.counts[k] > 0).map(([k, l]) => (
              <div key={k}>
                <dt>{l}</dt>
                <dd>{result.counts[k]}</dd>
              </div>
            ))}
          </dl>
          {result.warnings.length > 0 && (
            <Alert tone="warning" title={`E’tibor bering (${result.warnings.length})`}>
              <ul className="cn-import__warn">
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
          {done && done.staff.length > 0 && (
            <div className="cn-import__pins">
              <Alert tone="warning" title="Xodimlarning yangi PIN kodlari — faqat hozir ko‘rinadi">
                Nusxalab egaga bering. Keyin ega panelda “Xodimlar”dan o‘zgartira oladi.
              </Alert>
              <table className="cn-table">
                <thead>
                  <tr>
                    <th>Xodim</th>
                    <th>Rol</th>
                    <th>PIN</th>
                  </tr>
                </thead>
                <tbody>
                  {done.staff.map((s) => (
                    <tr key={s.name}>
                      <td>{s.name}</td>
                      <td>{s.role}</td>
                      <td className="op-num">
                        <b>{s.pin}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <CopyButton text={done.staff.map((s) => `${s.name} (${s.role}): ${s.pin}`).join('\n')} label="PIN ro‘yxatini nusxalash" />
            </div>
          )}
          {!done && result.staff.length > 0 && (
            <p className="cn-muted">Xodimlar: {result.staff.map((s) => `${s.name} (${s.role})`).join(', ')}. Har biriga yangi PIN beriladi.</p>
          )}
          {img && (
            <p className={img.failed.length ? 'cn-import__img cn-import__img--warn' : 'cn-import__img'}>
              Rasmlar: {img.ok} / {img.total} ko‘chirildi
              {img.failed.length > 0 && ` · ko‘chmadi: ${img.failed.join(', ')} (panelda qo‘lda qo‘yiladi)`}
            </p>
          )}
        </section>
      )}
    </Modal>
  );
}
